import { useState, useEffect, memo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Logo } from '../Logo'

interface HeaderProps {
  isAuthenticated: boolean
  onDashboardClick: () => void
  onSignUpClick: () => void
}

// Navigation links configuration
const anchorLinks = [
  { href: '#demo', label: 'Demo' },
  { href: '#features', label: 'Features' },
  { href: '#pricing', label: 'Pricing' },
]

export const Header = memo(function Header({ 
  isAuthenticated, 
  onDashboardClick,
  onSignUpClick 
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const isOnLandingPage = location.pathname === '/'

  // Handle scroll effect for blur/opacity
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setIsScrolled(scrollPosition > 50)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Smooth scroll to anchor, or navigate to landing page first
  const handleAnchorClick = (href: string) => {
    if (isOnLandingPage) {
      const element = document.querySelector(href)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } else {
      navigate('/' + href)
    }
    setIsMobileMenuOpen(false)
  }

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-bg/80 backdrop-blur-md border-b border-border/50 shadow-lg shadow-bg/20'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/"><Logo size="md" /></Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" role="navigation" aria-label="Main navigation">
            {anchorLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleAnchorClick(link.href)}
                className="text-text-muted hover:text-text transition-colors duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg rounded px-2 py-1"
                aria-label={`Navigate to ${link.label} section`}
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/documentation"
              className="text-text-muted hover:text-text transition-colors duration-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg rounded px-2 py-1"
            >
              Docs
            </Link>
          </nav>

          {/* Action Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <button
                onClick={onDashboardClick}
                className="btn btn-primary btn-sm transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-accent/20 active:scale-[0.99] transform-gpu"
                aria-label="Go to dashboard"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={onDashboardClick}
                  className="btn btn-ghost btn-sm"
                  aria-label="Sign in to your account"
                >
                  Sign In
                </button>
                <button
                  onClick={onSignUpClick}
                  className="btn btn-primary btn-sm transition-all duration-200 hover:scale-[1.01] hover:shadow-lg hover:shadow-accent/20 active:scale-[0.99] transform-gpu"
                  aria-label="Sign up for a free account"
                >
                  Get Started
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-3 text-text-muted hover:text-text transition-colors min-h-[44px] min-w-[44px] focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg rounded"
            aria-label={isMobileMenuOpen ? 'Close mobile menu' : 'Open mobile menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 7H21M3 12H21M3 17H21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          id="mobile-menu"
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="px-4 pt-4 pb-6 space-y-2 bg-panel/95 backdrop-blur-md border-t border-border/50 rounded-b-lg">
            {anchorLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleAnchorClick(link.href)}
                className="block w-full text-left px-4 py-3 text-text-muted hover:text-text hover:bg-bg/50 rounded-lg transition-colors duration-200 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-panel"
                aria-label={`Navigate to ${link.label} section`}
              >
                {link.label}
              </button>
            ))}
            <Link
              to="/documentation"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left px-4 py-3 text-text-muted hover:text-text hover:bg-bg/50 rounded-lg transition-colors duration-200 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-panel"
            >
              Docs
            </Link>
            <div className="pt-4 border-t border-border/50 mt-4 space-y-2">
              {isAuthenticated ? (
                <button
                  onClick={() => {
                    onDashboardClick()
                    setIsMobileMenuOpen(false)
                  }}
                  className="btn btn-primary btn-lg w-full"
                  aria-label="Go to dashboard"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      onDashboardClick()
                      setIsMobileMenuOpen(false)
                    }}
                    className="btn btn-secondary btn-lg w-full"
                    aria-label="Sign in to your account"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      onSignUpClick()
                      setIsMobileMenuOpen(false)
                    }}
                    className="btn btn-primary btn-lg w-full"
                    aria-label="Sign up for a free account"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
})
